import { useTranslation } from 'react-i18next';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { RoutesetterPicker, type RoutesetterSelection } from '../../../components/routesetters/RoutesetterPicker';
import { RouteTypeIcon } from '../../../components/icons/route-type-icon';
import type { CragRouteGrade } from '../../../types/crag-route';
import { ROUTE_TYPES } from '../../../types/route-type';
import { MAX_ROUTE_BOLTS, parseRouteBolts } from './route-bolts';
import { MAX_ROUTE_LENGTH, parseRouteLength, type NearbyRouteLength, type NearbyRouteNumber } from './route-length';

/** Matches `Route::MAX_CRAG_DESCRIPTION` — the server refuses more. */
const MAX_DESCRIPTION = 255;

/** The route's own fields, held by the page so the drawing shares its save. */
export interface CragRouteDraft {
  name: string;
  gradeId: string;
  routeSetter: RoutesetterSelection | null;
  description: string;
  /** Metres, as typed — see `parseRouteLength` for why it is not a number. */
  length: string;
  /** Also as typed, for the same reason. */
  bolts: string;
  routeTypes: string[];
}

interface CragRouteFormProps {
  draft: CragRouteDraft;
  onDraftChange: (patch: Partial<CragRouteDraft>) => void;
  grades: CragRouteGrade[];
  /** The lengths of the two routes immediately either side of this one. */
  nearbyLengths: NearbyRouteLength[];
  /** The bolts of those same two routes. */
  nearbyBolts: NearbyRouteNumber[];
  /** The crag, for the setter picker's list of who works here. */
  cragSlug: string;
  onSave: () => void;
  /** Absent while the route is still being created — there is nothing to remove. */
  onDelete?: () => void;
  saving: boolean;
  deleting?: boolean;
  error?: string | null;
}

/**
 * A route's details, at the foot of the controls column: what it is called, how
 * hard it is, who bolted it and what to know before setting off.
 *
 * Under the drawing tools rather than in a column of its own, because naming a
 * line and tracing it are two halves of describing the same route — and a
 * fourth column for four fields would take the room the photo needs.
 */
export function CragRouteForm({
  draft,
  onDraftChange,
  grades,
  nearbyLengths,
  nearbyBolts,
  cragSlug,
  onSave,
  onDelete,
  saving,
  deleting,
  error,
}: CragRouteFormProps) {
  const { t } = useTranslation();

  const { invalid: lengthInvalid } = parseRouteLength(draft.length);
  const { invalid: boltsInvalid } = parseRouteBolts(draft.bolts);
  const saveable = draft.name.trim().length > 0 && draft.gradeId.length > 0 && !lengthInvalid && !boltsInvalid;
  const descriptionLength = draft.description.trim().length;

  const setRouteType = (type: string, checked: boolean) => {
    const others = draft.routeTypes.filter((other) => other !== type);
    onDraftChange({ routeTypes: checked ? [...others, type] : others });
  };

  return (
    <div className="space-y-4 border-t pt-3" data-testid="crag-route-form">
      <div className="space-y-2">
        <Label htmlFor="crag-route-name">{t('cragAdmin.routes.name')}</Label>
        <Input
          id="crag-route-name"
          value={draft.name}
          onChange={(event) => onDraftChange({ name: event.target.value })}
          placeholder={t('cragAdmin.routes.namePlaceholder')}
        />
      </div>

      {/* The whole ladder laid out rather than folded into a select: a grade is
          picked against the ones either side of it. One at a time, so an armed
          chip cannot be turned off — only replaced. */}
      <div className="space-y-2">
        <Label id="crag-route-grade-label">{t('cragAdmin.routes.grade')}</Label>
        <ToggleGroup
          type="single"
          value={draft.gradeId}
          onValueChange={(gradeId: string) => {
            if (gradeId) onDraftChange({ gradeId });
          }}
          aria-labelledby="crag-route-grade-label"
          className="flex flex-wrap gap-1.5 rounded-md"
        >
          {grades.map((grade) => (
            <ToggleGroupItem
              key={grade.id}
              value={grade.id}
              aria-label={grade.name}
              className={`min-w-9 rounded-md border px-2 py-1 text-xs transition-colors ${
                draft.gradeId === grade.id
                  ? 'border-primary bg-primary font-semibold text-primary-foreground'
                  : 'border-input bg-background text-foreground hover:bg-muted'
              }`}
            >
              {grade.name}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {grades.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('cragAdmin.routes.noGrades')}</p>
        )}
      </div>

      {/* How much rope the line eats, which is the other thing a climber wants
          off the topo before racking up. Optional: plenty of lines were bolted
          long before anyone thought to measure them. */}
      <div className="space-y-2">
        <Label htmlFor="crag-route-length">{t('cragAdmin.routes.length')}</Label>
        <div className="relative">
          <Input
            id="crag-route-length"
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_ROUTE_LENGTH}
            step={1}
            value={draft.length}
            onChange={(event) => onDraftChange({ length: event.target.value })}
            placeholder={t('cragAdmin.routes.lengthPlaceholder')}
            aria-invalid={lengthInvalid}
            aria-describedby={lengthInvalid ? 'crag-route-length-error' : undefined}
            className="pr-9"
          />
          <span
            className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground"
            aria-hidden
          >
            {t('cragAdmin.routes.lengthUnit')}
          </span>
        </div>

        {lengthInvalid && (
          <p id="crag-route-length-error" className="text-sm text-destructive">
            {t('cragAdmin.routes.lengthInvalid', { max: MAX_ROUTE_LENGTH })}
          </p>
        )}

        {/* One tap rather than a retype: a cliff is as tall as it is, so the
            line next to a 25m route is almost always 25m too. Only the routes
            either side of this one — see `nearbyRouteLengths`.

            Each chip names its side rather than sitting under a "nearby" label:
            two numbers with no hand on them are a pair of guesses, while "SX
            30m" is a claim that can be checked against the cliff. Two of them
            at most, so the row never needs to wrap. */}
        <NeighbourChips
          chips={nearbyLengths.map(({ side, length }) => ({
            side,
            value: length,
            label: t(side === 'left' ? 'cragAdmin.routes.lengthLeft' : 'cragAdmin.routes.lengthRight', {
              value: length,
            }),
          }))}
          onPick={(value) => onDraftChange({ length: String(value) })}
        />
      </div>

      {/* How many quickdraws to rack, and offered from the neighbours like the
          length: a wall of one height tends to be bolted alike. */}
      <div className="space-y-2">
        <Label htmlFor="crag-route-bolts">{t('cragAdmin.routes.bolts')}</Label>
        <Input
          id="crag-route-bolts"
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_ROUTE_BOLTS}
          step={1}
          value={draft.bolts}
          onChange={(event) => onDraftChange({ bolts: event.target.value })}
          placeholder={t('cragAdmin.routes.boltsPlaceholder')}
          aria-invalid={boltsInvalid}
          aria-describedby={boltsInvalid ? 'crag-route-bolts-error' : undefined}
        />

        {boltsInvalid && (
          <p id="crag-route-bolts-error" className="text-sm text-destructive">
            {t('cragAdmin.routes.boltsInvalid', { max: MAX_ROUTE_BOLTS })}
          </p>
        )}

        <NeighbourChips
          chips={nearbyBolts.map(({ side, value }) => ({
            side,
            value,
            label: t(side === 'left' ? 'cragAdmin.routes.boltsLeft' : 'cragAdmin.routes.boltsRight', { value }),
          }))}
          onPick={(value) => onDraftChange({ bolts: String(value) })}
        />
      </div>

      <div className="space-y-2">
        <Label id="crag-route-types-label">{t('cragAdmin.routes.routeTypes')}</Label>
        <div
          role="group"
          aria-labelledby="crag-route-types-label"
          className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-x-3 gap-y-2"
        >
          {ROUTE_TYPES.map((type) => (
            <label key={type} className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={draft.routeTypes.includes(type)}
                onCheckedChange={(checked) => setRouteType(type, checked === true)}
              />
              <RouteTypeIcon type={type} className="size-4 shrink-0 text-muted-foreground" />
              {t(`routeTypes.${type}`)}
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t('cragAdmin.routes.routeSetter')}</Label>
        <RoutesetterPicker
          mode="single"
          value={draft.routeSetter ? [draft.routeSetter] : []}
          onChange={(next: RoutesetterSelection[]) => onDraftChange({ routeSetter: next[0] ?? null })}
          locationId={cragSlug}
          placeholder={t('cragAdmin.routes.routeSetterPlaceholder')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="crag-route-description">{t('cragAdmin.routes.description')}</Label>
        <Textarea
          id="crag-route-description"
          rows={3}
          maxLength={MAX_DESCRIPTION}
          value={draft.description}
          onChange={(event) => onDraftChange({ description: event.target.value })}
        />
        {/* Red rather than a refusal: an older, longer one can be left alone while the
            rest of the route is edited, and the server only minds it once it changes. */}
        <p
          data-testid="crag-route-description-count"
          className={`text-right text-xs ${descriptionLength > MAX_DESCRIPTION ? 'text-destructive' : 'text-muted-foreground'}`}
        >
          {descriptionLength}/{MAX_DESCRIPTION}
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center gap-2">
        <Button type="button" size="sm" onClick={onSave} disabled={saving || !saveable}>
          {saving && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
          {t('cragAdmin.routes.save')}
        </Button>

        {onDelete && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto text-destructive hover:text-destructive"
            onClick={onDelete}
            disabled={deleting}
          >
            {deleting ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="size-4" aria-hidden />
            )}
            <span className="sr-only">{t('cragAdmin.routes.delete')}</span>
          </Button>
        )}
      </div>
    </div>
  );
}

/** A neighbour's figure, one tap from being this route's as well. */
function NeighbourChips({
  chips,
  onPick,
}: {
  chips: { side: 'left' | 'right'; value: number; label: string }[];
  onPick: (value: number) => void;
}) {
  if (chips.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      {chips.map(({ side, value, label }) => (
        <button
          key={side}
          type="button"
          onClick={() => onPick(value)}
          className="shrink-0 rounded-md border border-input bg-background px-2 py-0.5 text-xs text-foreground transition-colors hover:bg-muted"
        >
          {label}
        </button>
      ))}
    </div>
  );
}
